import React, { useEffect, useMemo, useState } from "react";
import { Popover } from "@mui/material";
import { Colors } from "../../../../Constants/colors";
import { BtnSize, BtnType, Button } from "../../../Button";
import { PREDEFINED_REMARKS } from "../../../../Constants/options/remarks.options";
import { getRemarkWithPronoun } from "../../../../Helper/remarks.helper";

interface Props {
  value: string;
  studentName: string;
  gender?: string | null;
  onCommit: (value: string) => unknown;
}

const RemarkIcon = ({ filled }: { filled: boolean }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 4h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H9l-5 4V5a1 1 0 0 1 1-1z"
      fill={filled ? Colors.TangerineOrange : "none"}
      fillOpacity={filled ? 0.15 : 0}
      stroke={filled ? Colors.TangerineOrange : Colors.Grey}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * A single, one-line-sentence remark doesn't fit comfortably inside a
 * narrow spreadsheet cell. Instead of forcing every row to reserve a wide
 * column, this renders a compact button showing a preview of the remark,
 * and typing happens in a roomy popover anchored to that row.
 *
 * The popover has just one text box: as the user types, the list of
 * frequently used remarks below it (pronoun adapted to the student's
 * gender) filters to matches, and clicking one fills the same box.
 */
export const RemarksCell = ({
  value,
  studentName,
  gender,
  onCommit,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const isOpen = Boolean(anchorEl);

  const suggestions = useMemo(
    () => PREDEFINED_REMARKS.map((remark) => getRemarkWithPronoun(remark, gender)),
    [gender],
  );

  const filteredSuggestions = useMemo(() => {
    const query = draft.trim().toLowerCase();
    if (!query) return suggestions;
    return suggestions.filter((suggestion) =>
      suggestion.toLowerCase().includes(query),
    );
  }, [suggestions, draft]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (draft !== (value || "")) {
      onCommit(draft);
    }
  };

  const hasValue = !!value?.trim();

  return (
    <>
      <button
        type="button"
        className={`rs-remarks-btn${hasValue ? " rs-remarks-btn-filled" : ""}`}
        onClick={handleOpen}
        title={hasValue ? value : "Add a remark"}
      >
        <RemarkIcon filled={hasValue} />
        <span className="rs-remarks-preview">
          {hasValue ? value : "Add remark"}
        </span>
      </button>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div className="rs-remarks-popover">
          <div className="rs-remarks-popover-title">
            Remark for <strong>{studentName}</strong>
          </div>

          <textarea
            className="rs-remarks-textarea"
            autoFocus
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Write a remark, or start typing to see suggestions..."
            rows={3}
          />

          <span className="rs-remarks-section-label">
            Frequently used remarks
          </span>
          <div className="rs-remarks-suggestion-list">
            {filteredSuggestions.length === 0 ? (
              <div className="rs-remarks-empty-suggestions">
                No matching remarks found.
              </div>
            ) : (
              filteredSuggestions.map((suggestion) => {
                const isSelected = draft === suggestion;
                return (
                  <button
                    type="button"
                    key={suggestion}
                    className={`rs-remarks-suggestion${
                      isSelected ? " rs-remarks-suggestion-selected" : ""
                    }`}
                    onClick={() => setDraft(suggestion)}
                  >
                    {suggestion}
                  </button>
                );
              })
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
            }}
          >
            <Button
              text="Done"
              btnType={BtnType.primary}
              btnSize={BtnSize.sm}
              onClick={handleClose}
            />
          </div>
        </div>
      </Popover>
    </>
  );
};
